import React from 'react';
import { Link } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  DocumentMagnifyingGlassIcon,
  DocumentTextIcon,
  FunnelIcon,
  BriefcaseIcon,
} from 'components/icons';

// Everything listed here must exist in the product today. If it's on the
// roadmap, it goes in `roadmap` below — not in a checkmark list. GovCon buyers
// verify claims, and About.tsx promises no inflated ones.

const features = [
  {
    title: 'Opportunity Discovery',
    description:
      'Federal solicitations pulled from SAM.gov, plus state solicitations from Florida and Alabama, scored against your NAICS codes, set-aside eligibility, and past work — so the list you open in the morning is already filtered.',
    icon: MagnifyingGlassIcon,
    items: [
      'Search across federal solicitations from SAM.gov, plus Florida and Alabama state solicitations',
      'Match scoring against your business profile',
      'Filter by NAICS, set-aside, agency, and procurement type',
      'Watch list for opportunities you are tracking',
    ],
  },
  {
    title: 'Change Tracking',
    description:
      'The part nobody else does. Clevor watches the opportunities you care about and tells you when something moves.',
    icon: ArrowPathIcon,
    items: [
      'Deadline changes flagged as they happen',
      'Amendments and new documents detected',
      'In-app notifications on watched opportunities',
      'No more re-checking SAM.gov to see what shifted',
    ],
  },
  {
    title: 'Document Intelligence',
    description:
      'Upload the solicitation package and Clevor reads it — extracting requirements, evaluation criteria, and submission instructions into something you can actually work from.',
    icon: DocumentMagnifyingGlassIcon,
    items: [
      'Requirement extraction from solicitation documents',
      'Evaluation criteria and Section L/M mapping where present',
      'Compliance matrix generation',
      'Answers cite the specific section they came from',
    ],
  },
  {
    title: 'Proposal Writer',
    description:
      'Generate proposal sections grounded in the actual RFP requirements and your own company material — then edit, review, and export.',
    icon: DocumentTextIcon,
    items: [
      'Technical approach, management plan, and past performance sections',
      'Grounded in extracted requirements, not generic templates',
      'Automated reviewer pass before you export',
      'DOCX export',
    ],
  },
  {
    title: 'Pipeline',
    description:
      'Track every opportunity from first look through submission without a spreadsheet.',
    icon: FunnelIcon,
    items: [
      'Stage-based pipeline from discovery to award',
      'Deadline visibility across everything you are pursuing',
      'Notes and status per opportunity',
      'Team access on Pro plans',
    ],
  },
  {
    title: 'Your Company Library',
    description:
      'The reusable material that makes each next proposal faster: past performance, boilerplate, and pricing assumptions in one place.',
    icon: BriefcaseIcon,
    items: [
      'Past performance library the proposal writer draws from',
      'Reusable boilerplate sections',
      'Pricing profile for rate and markup assumptions',
      'Grows more useful with every proposal',
    ],
  },
];

// Stated plainly instead of dressed up as shipped features.
const roadmap = [
  'SSO (SAML) and SCIM provisioning',
  'State and local sources beyond Florida and Alabama',
  'Slack and Teams notifications',
  'Formal security certifications',
];

const Features: React.FC = () => {
  return (
    <div className="bg-surface py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-overline uppercase text-brand-600">How It Works</h2>
          <p className="mt-2 text-display text-content sm:text-display-lg text-balance">
            The manual work, replaced
          </p>
          <p className="mt-6 text-lg leading-8 text-content-muted">
            Clevor covers the daily workflow between "a contract got posted" and "we submitted a
            proposal" — monitoring, tracking changes, reading the documents, and drafting from
            what they actually require.
          </p>
        </div>

        <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
          <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-10 lg:max-w-none lg:grid-cols-2">
            {features.map((feature) => (
              <div key={feature.title} className="flex flex-col">
                <dt className="flex items-center gap-x-3 text-xl font-semibold leading-7 text-content">
                  <feature.icon className="h-6 w-6 flex-none text-brand-600" aria-hidden="true" />
                  {feature.title}
                </dt>
                <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-content-muted">
                  <p className="flex-auto">{feature.description}</p>
                  <ul className="mt-4 space-y-2">
                    {feature.items.map((item) => (
                      <li key={item} className="flex items-start">
                        <svg
                          className="h-4 w-4 text-brand-600 mr-2 mt-1.5 flex-none"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                          aria-hidden="true"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Roadmap — stated as not-yet-shipped, on purpose */}
        <div className="mx-auto mt-24 max-w-3xl">
          <div className="rounded-2xl bg-surface-sunk ring-1 ring-border p-8">
            <h3 className="text-title text-content">Not built yet</h3>
            <p className="mt-2 text-sm text-content-muted">
              Clevor is in active development and we would rather tell you what is missing than
              let you find out later. These are on the roadmap and not available today:
            </p>
            <ul className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {roadmap.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-content-muted">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-gray-300" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-16 text-center">
          <Link to="/signup" className="btn-primary px-8 py-3.5 text-base">
            Get Early Access
          </Link>
          <p className="mt-4 text-sm text-content-muted">
            No credit card required. Or{' '}
            <Link to="/pricing" className="font-semibold text-brand-600 hover:text-brand-700">
              see pricing
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
};

export default Features;
